import { atom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect, type RefObject } from 'react';
import { useSectionInView } from '~/modules/enquiry/bulk/use-section-in-view';

/**
 * Whether the reader has arrived at the gate.
 *
 * An atom rather than a prop because the two parties are at opposite ends of
 * the tree: the panel is rendered deep inside the grid, by four different
 * callers, and the chrome that has to answer for it is the page shell.
 */
const contentGateReachedAtom = atom(false);

/**
 * For page chrome that stops applying once the grid ends at the gate — the
 * filter and category bar above all, which past this point offers navigation
 * into a catalogue the reader cannot open.
 */
export const useContentGateReached = (): boolean =>
    useAtomValue(contentGateReachedAtom);

/**
 * Reports the gate's arrival, from the panel.
 *
 * Reads the same middle-of-the-viewport line the Magic Island uses, which for
 * the run below is the moment its copy pins — so the bar goes as the gate
 * settles rather than at some unrelated offset. Clears on unmount so leaving
 * the page cannot strand the chrome hidden.
 */
export const useReportContentGateReached = (
    runRef: RefObject<HTMLElement | null>,
): void => {
    const reached = useSectionInView(runRef);
    const setReached = useSetAtom(contentGateReachedAtom);

    useEffect(() => {
        setReached(reached);
        return () => setReached(false);
    }, [reached, setReached]);
};
